import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, AutoComplete, Button, Input, message, Skeleton, Space, Table, Typography } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import * as React from 'react';
import InstitutionUsersApiService from '../../../api/InstitutionUsersApiService';
import NoticeOfIntentChangeRequestApiService from '../../../api/NoticeOfIntentChangeRequestApiService';
import * as SaveNoticeOfIntentChangeRequestStep3Handler from '../../../handlerModels/SaveNoticeOfIntentChangeRequestStep3Handler';
import * as SearchForConsultantsHandler from '../../../handlerModels/SearchForConsultantsHandler';
import * as SearchForUsersForNoticeOfIntentHandler from '../../../handlerModels/SearchForUsersForNoticeOfIntentHandler';
import * as SubmitNoticeOfIntentChangeRequestStep3Handler from '../../../handlerModels/SubmitNoticeOfIntentChangeRequestStep3Handler';
import ChangeRequestDTO from '../../../models/ChangeRequestDTO';
import NoticeOfIntentChangeRequestConsultantMeetingConsultantDTO from '../../../models/NoticeOfIntentChangeRequestConsultantMeetingConsultantDTO';
import NoticeOfIntentChangeRequestConsultantMeetingDTO from '../../../models/NoticeOfIntentChangeRequestConsultantMeetingDTO';
import * as GetNoticeOfIntentChangeRequestStep3Handler from '../../../handlerModels/GetNoticeOfIntentChangeRequestStep3Handler';
import NoticeOfIntentChangeRequestContactDTO from '../../../models/NoticeOfIntentChangeRequestContactDTO';
import NoticeOfIntentChangeRequestDTO from '../../../models/NoticeOfIntentChangeRequestDTO';
import UserDTO from '../../../models/UserDTO';
import BaseChangeRequestProps from '../../../redux/bases/BaseChangeRequestProps';
import BaseFormProps from '../../../redux/bases/BaseFormProps';
import BaseFormState from '../../../redux/bases/BaseFormState';
import Guid from '../../../utils/Guid';
import ValidationRuleUtil from '../../../utils/ValidationRuleUtil';
import ValidationUtil from '../../../utils/ValidationUtil';
import { FilterType } from '../../datatables/core/DataTable';
import GoBackButton from '../../buttons/GoBackButton';
import ResetButton from '../../buttons/ResetButton';
import SaveAndContinueButton from '../../buttons/SaveAndContinueButton';
import SaveButton from '../../buttons/SaveButton';

interface NoticeOfIntentChangeRequestStep3FormState extends BaseFormState {
  changeRequest: ChangeRequestDTO;
  noticeOfIntent: NoticeOfIntentChangeRequestDTO;
  userSearch: UserDTO[];
  consultantSearch: UserDTO[];
  openSelect: Map<string, boolean>;
}

interface NoticeOfIntentChangeRequestStep3FormProps extends BaseFormProps, BaseChangeRequestProps {
  changeRequestId: string | null;
  institutionId: string | null;
  onSave?: () => void;
  onSubmit?: () => void;
  onPrevious?: () => void;
  onChange?: (altered: boolean) => void;
  readonly?: boolean;
}

class NoticeOfIntentChangeRequestStep3Form extends React.Component<NoticeOfIntentChangeRequestStep3FormProps, NoticeOfIntentChangeRequestStep3FormState> {
  private readonly _formRef = React.createRef<FormInstance>();
  firstNameIdentifier = 'firstName';
  lastNameIdentifier = 'lastName';
  emailIdentifier = 'email';

  private getContactFormItems = (index: number) => {
    const fields = new Map<string, FormItemProps>()
      .set(NoticeOfIntentChangeRequestContactDTO.userId, {
        name: [index, NoticeOfIntentChangeRequestContactDTO.userId],
      })
      .set(NoticeOfIntentChangeRequestContactDTO.firstName, {
        name: [index, NoticeOfIntentChangeRequestContactDTO.firstName],
        required: true,
        rules: [ValidationRuleUtil.required()]
      })
      .set(NoticeOfIntentChangeRequestContactDTO.lastName, {
        name: [index, NoticeOfIntentChangeRequestContactDTO.lastName],
        required: true,
        rules: [ValidationRuleUtil.required()]
      })
      .set(NoticeOfIntentChangeRequestContactDTO.email, {
        name: [index, NoticeOfIntentChangeRequestContactDTO.email],
        required: true,
        rules: [ValidationRuleUtil.required()]
      })
      .set(NoticeOfIntentChangeRequestContactDTO.phone, {
        name: [index, NoticeOfIntentChangeRequestContactDTO.phone],
      });
    return fields;
  }

  private getColumnDefinitionsForContactInformation = (remove: any) => {
    const contactOptions = this.state.userSearch;
    const autoCompleteOptions: any[] = contactOptions.map(x => {
      return { label: x.fullDisplay, key: x.id, value: x.id }
    });

    return [
      {
        title: 'First Name',
        dataIndex: NoticeOfIntentChangeRequestContactDTO.firstName,
        render: (data: string, row: any, index: any) => {
          return <FormItem
            {...this.getContactFormItems(index).get(NoticeOfIntentChangeRequestContactDTO.firstName)}
            {...ValidationUtil.getValidation(NoticeOfIntentChangeRequestContactDTO.firstName + index, this.state.fieldErrors, this.state.submitted || this.props.readonly)}>
            <AutoComplete
              open={this.state.openSelect.get(this.firstNameIdentifier + index)}
              onClick={() => this.handleClick(this.firstNameIdentifier + index)}
              onBlur={() => this.handleBlur(this.firstNameIdentifier + index)}
              disabled={this.props.readonly}
              options={autoCompleteOptions}
              onSearch={this.handleUserSearch}
              onSelect={(e) => this.handleContactSelected(e, contactOptions, index)}>
              <Input title='First Name' disabled={this.props.readonly} />
            </AutoComplete>
          </FormItem>
        },
      },
      {
        title: 'Last Name',
        dataIndex: NoticeOfIntentChangeRequestContactDTO.lastName,
        render: (data: string, row: any, index: any) => {
          return <FormItem
            {...this.getContactFormItems(index).get(NoticeOfIntentChangeRequestContactDTO.lastName)}
            {...ValidationUtil.getValidation(NoticeOfIntentChangeRequestContactDTO.lastName + index, this.state.fieldErrors, this.state.submitted || this.props.readonly)}
          >
            <AutoComplete
              open={this.state.openSelect.get(this.lastNameIdentifier + index)}
              onClick={() => this.handleClick(this.lastNameIdentifier + index)}
              onBlur={() => this.handleBlur(this.lastNameIdentifier + index)}
              disabled={this.props.readonly}
              options={autoCompleteOptions}
              onSearch={this.handleUserSearch}
              onSelect={(e) => this.handleContactSelected(e, contactOptions, index)}>
              <Input title='Last Name' disabled={this.props.readonly} />
            </AutoComplete>
          </FormItem>
        },
      },
      {
        title: 'Email',
        dataIndex: NoticeOfIntentChangeRequestContactDTO.email,
        render: (data: string, row: any, index: any) => {
          return <FormItem
            {...this.getContactFormItems(index).get(NoticeOfIntentChangeRequestContactDTO.email)}
            {...ValidationUtil.getValidation(NoticeOfIntentChangeRequestContactDTO.email + index, this.state.fieldErrors, this.state.submitted || this.props.readonly)}>
            <AutoComplete
              open={this.state.openSelect.get(this.emailIdentifier + index)}
              onClick={() => this.handleClick(this.emailIdentifier + index)}
              onBlur={() => this.handleBlur(this.emailIdentifier + index)}
              disabled={this.props.readonly}
              options={autoCompleteOptions}
              onSearch={this.handleUserSearch}
              onSelect={(e) => this.handleContactSelected(e, contactOptions, index)}>
              <Input title='Email' disabled={this.props.readonly} />
            </AutoComplete>
          </FormItem>
        },
      },
      {
        title: 'Phone Number',
        dataIndex: NoticeOfIntentChangeRequestContactDTO.phone,
        render: (data: string, row: any, index: any) => {
          return <FormItem
            {...this.getContactFormItems(index).get(NoticeOfIntentChangeRequestContactDTO.phone)}
            {...ValidationUtil.getValidation(NoticeOfIntentChangeRequestContactDTO.phone + index, this.state.fieldErrors, this.state.submitted || this.props.readonly)}>
            <Input title='Phone Number' disabled={this.props.readonly}></Input>
          </FormItem>
        },
      },
      {
        title: 'Actions',
        dataIndex: NoticeOfIntentChangeRequestContactDTO.userId,
        width: '15px',
        render: (data: string, row: any) => {
          return this.props.readonly || this._formRef.current?.getFieldValue(NoticeOfIntentChangeRequestDTO.contacts)?.length <= 1 ?
            null :
            <Button type="link" onClick={() => remove(row.name)}><DeleteOutlined /></Button>
        },
        sorter: false,
        filterType: FilterType.NONE
      }
    ] as any[];
  };

  constructor(props: NoticeOfIntentChangeRequestStep3FormProps) {
    super(props);

    this.state = {
      changeRequest: ChangeRequestDTO.create(),
      noticeOfIntent: NoticeOfIntentChangeRequestDTO.create(),
      consultantSearch: [],
      userSearch: [],
      openSelect: new Map<string, boolean>(),
    };
  }

  componentDidMount() {
    if (this.props.changeRequestId && this.props.changeRequestId != Guid.Empty()) {
      this.loadChangeRequest();
    }
  }

  componentDidUpdate(prevProps: NoticeOfIntentChangeRequestStep3FormProps) {
    if (this.props.changeRequestId && this.props.changeRequestId != Guid.Empty() && this.props.changeRequestId != prevProps.changeRequestId) {
      this.loadChangeRequest();
    }

    if (this.props.loading != prevProps.loading) {
      this.setState({
        loading: this.props.loading
      });
    }
  }

  private loadChangeRequest = () => {
    const request = GetNoticeOfIntentChangeRequestStep3Handler.Request.create({
      changeRequestId: this.props.changeRequestId
    });

    return NoticeOfIntentChangeRequestApiService.getStep3(request)
      .then((results: GetNoticeOfIntentChangeRequestStep3Handler.Result) => {
        if (results) {

          const noi = results.changeRequest?.noticeOfIntentChangeRequest ?? NoticeOfIntentChangeRequestDTO.create();

          if (noi.contacts?.length == 0) {
            noi.contacts = [
              NoticeOfIntentChangeRequestContactDTO.create()
            ]
          }

          if (noi.consultantMeetings?.length == 0) {
            noi.consultantMeetings = [
              NoticeOfIntentChangeRequestConsultantMeetingDTO.create({
                consultants: [
                  NoticeOfIntentChangeRequestConsultantMeetingConsultantDTO.create()
                ],
                date: ''
              })
            ]
          }

          const open = this.state.openSelect;
          let count = 0;
          noi.contacts?.forEach(() => {
            open.set(this.firstNameIdentifier + count, false);
            open.set(this.lastNameIdentifier + count, false);
            open.set(this.emailIdentifier + count, false);
            count++;
          });

          this.setState({
            changeRequest: results.changeRequest ?? ChangeRequestDTO.create(),
            noticeOfIntent: results.changeRequest?.noticeOfIntentChangeRequest ?? NoticeOfIntentChangeRequestDTO.create(),
            fieldErrors: this.props.fieldErrors,
          }, () => this.resetForm());
        }
      }).catch(() => {
        this.setState({ error: true, message: 'Could not load change request.' });
      });
  }

  toggleOpen = (key: string, state: boolean) => {
    const open = this.state.openSelect;
    open.set(this.firstNameIdentifier, false);
    open.set(this.lastNameIdentifier, false);
    open.set(this.emailIdentifier, false);
    open.set(key, state);

    this.setState({
      openSelect: open
    });
  }

  handleClick = (key: string) => {
    this.toggleOpen(key, !(this.state.openSelect?.get(key) ?? false));
  }

  handleBlur = (key: string) => {
    this.toggleOpen(key, false);
  }


  public resetForm = () => {
    this.setState({
      altered: false,
    });

    this._formRef.current?.resetFields();
    if (this.props.onChange) {
      this.props.onChange(false)
    }
  }

  private handleContactSelected = (value: string, options: UserDTO[], index: number) => {
    const selected = options.filter(x => x.id == value)[0];

    const contacts = this._formRef.current?.getFieldValue(NoticeOfIntentChangeRequestDTO.contacts);
    contacts[index] = {
      ...contacts[index],
      firstName: selected.firstName,
      lastName: selected.lastName,
      phoneNumber: selected.phoneNumber,
      email: selected.username,
      userId: selected.id
    }

    if (selected) {
      this._formRef.current?.setFieldsValue({
        contacts: contacts,
      });
    }

    this.setState({
      userSearch: []
    });
  }

  private handleUserSearch = (searchText: string) => {
    if (searchText.length >= 3) {
      const request = SearchForUsersForNoticeOfIntentHandler.Request.create({
        institutionId: this.props.institutionId,
        searchText: searchText,
      })

      InstitutionUsersApiService.searchForUsers(request)
        .then((results: SearchForUsersForNoticeOfIntentHandler.Result) => {
          if (results.users) {
            this.setState({ userSearch: results.users })
          }
        })
        .catch(() => {
          this.setState({ error: true });
        })
        .finally(() => {
          this.setState({ loading: false })
        });
    }
  }

  private handleConsultantSearch = (searchText: string) => {
    if (searchText.length >= 3) {
      const request = SearchForConsultantsHandler.Request.create({
        institutionId: this.props.institutionId,
        searchText: searchText,
      })

      InstitutionUsersApiService.searchForConsultants(request)
        .then((results: SearchForConsultantsHandler.Result) => {
          if (results.users) {
            this.setState({ consultantSearch: results.users })
          }
        })
        .catch(() => {
          this.setState({ error: true });
        })
        .finally(() => {
          this.setState({ loading: false })
        });
    }
  }

  private handleSave = () => {
    this.setState({ saving: true });

    const model = NoticeOfIntentChangeRequestDTO.create(this._formRef ? (this._formRef.current as any).getFieldsValue() : null);
    model.changeRequestId = this.props.changeRequestId ?? Guid.Empty();

    const request = SaveNoticeOfIntentChangeRequestStep3Handler.Request.create({
      noticeOfIntentChangeRequest: model,
    });

    NoticeOfIntentChangeRequestApiService.saveStep3(request)
      .then((result: SaveNoticeOfIntentChangeRequestStep3Handler.Result) => {
        if (result?.succeeded) {
          message.success('Saved');
        }
        else {
          this.setState({
            error: !result?.succeeded,
            message: result?.errors.join('\n'),
            fieldErrors: result?.fieldErrors
          });
          message.error('Save Failed');
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Save Failed');
      })
      .finally(() => {
        this.setState({ loading: false, saving: false, altered: false });
      });
  }

  private handleSubmit = () => {
    this.setState({ submitting: true });

    const model = NoticeOfIntentChangeRequestDTO.create(this._formRef ? (this._formRef.current as any).getFieldsValue() : null) as NoticeOfIntentChangeRequestDTO;
    model.changeRequestId = this.props.changeRequestId ?? Guid.Empty();

    const request = SubmitNoticeOfIntentChangeRequestStep3Handler.Request.create({
      noticeOfIntentChangeRequest: model,
    });

    NoticeOfIntentChangeRequestApiService.submitStep3(request)
      .then((result: SubmitNoticeOfIntentChangeRequestStep3Handler.Result) => {
        this.setState({ submitted: true });

        if (result?.succeeded) {
          message.success('Saved');

          if (this.props.onSubmit) {
            this.props.onSubmit();
          }
        }
        else {
          this.setState({
            error: !result?.succeeded,
            message: result?.errors.join('\n'),
            fieldErrors: result?.fieldErrors
          });
          message.error('Save Failed');
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Save Failed');
      })
      .finally(() => {
        this.setState({ loading: false, submitting: false });
      });
  }

  private handleChange = () => {
    this.setState({ altered: true });
    if (this.props.onChange) {
      this.props.onChange(true)
    }
  }

  render() {
    if (this.state.loading) {
      return <Skeleton active={true} />;
    }

    return (
      <Space size="small" direction="vertical">
        {this.renderErrors()}


        <Form ref={this._formRef}
          layout="vertical"
          initialValues={this.state.noticeOfIntent}
          onValuesChange={this.handleChange}
          onFinish={this.handleSubmit}>

          <Space direction="vertical" >
            <Typography.Paragraph italic={true}>
              Please provide the contact information for the point(s) of contact for this program.
            </Typography.Paragraph>

            <Form.List name={NoticeOfIntentChangeRequestDTO.contacts}>
              {
                (contacts, { add, remove }) => {
                  return (
                    <Space direction='vertical'>
                      <Table
                        size='small'
                        dataSource={contacts}
                        pagination={false}
                        columns={this.getColumnDefinitionsForContactInformation(remove)} />
                      {this.renderAddAdditionalContact(add)}
                    </Space>);
                }
              }
            </Form.List>

            {this.renderActions()}
          </Space>
        </Form >
      </Space >
    );
  }

  renderActions() {
    if (!this.props.readonly) {
      return (
        <Space direction='horizontal' wrap={true}>
          <GoBackButton onClick={this.props.onPrevious} />
          <SaveAndContinueButton submitting={this.state.submitting} />
          <SaveButton type='default' htmlType='button' onClick={this.handleSave} saving={this.state.submitting} saved={this.state.saved} />
          <ResetButton resetting={this.state.resetting} onConfirm={this.resetForm} />
        </Space>
      );
    }
  }

  renderAddAdditionalContact(add: any) {
    if (!this.props.readonly) {
      return (
        <Button onClick={() => add()} icon={<PlusOutlined />}>
          Add Additional Contact
        </Button>
      );
    }
  }

  renderErrors() {
    if (this.state.error) {
      return <Alert type="error" message='Error' showIcon={true} description={this.state.message} />;
    }
  }
}

export default NoticeOfIntentChangeRequestStep3Form